import React, { useState, useMemo } from "react";
import axios from "axios";

const DeepLinkTool = ({ backEndUrl, encodedState }) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const toSlug = (s) => s.replace(/\s+/g, "-").toLowerCase();

    // Items:
    //  - First 5: ltiResourceLink (gradable; scoreMaximum 100 => percentage-friendly)
    //  - Next 3: ltiResourceLink WITH embed.html + iframe.width=800 (also gradable)
    //  - Next 3: inline html
    //  - Last 2: link with embed.html + iframe (YouTube)
    const items = useMemo(() => {
        const lti = Array.from({ length: 5 }, (_, i) => {
            const title = `LTI Assignment ${i + 1}`;
            const slug = toSlug(title);
            return {
                id: `lti-${i + 1}`,
                type: "ltiResourceLink",
                label: title,
                title,
                url: `${backEndUrl}/lti/launch/${slug}`,
                custom: { assignmentName: title, route: "LTIAssignment" },
                lineItem: {
                    resourceId: slug,
                    label: `LTI Gradable Item ${title}`,
                    scoreMaximum: 100.0, // percentage-friendly
                },
            };
        });

        // NEW: a few (3) LTI items that ALSO carry embed.html + iframe (width=800)
        const ltiWithEmbed2 = Array.from({ length: 3 }, (_, i) => {
            const n = i + 1;
            const title = `LTI Assignment (Embed) ${n}`;
            const slug = toSlug(title);
            const previewSrc = `${backEndUrl}/lti/preview/${slug}`;
            return {
                id: `lti-embed-${n}`,
                type: "ltiResourceLink",
                label: title,
                title,
                url: `${backEndUrl}/lti/launch/${slug}`,
                custom: { assignmentName: title, route: "CourseDetails" },
                lineItem: {
                    resourceId: slug,
                    label: `LTI Gradable Item ${title}`,
                    scoreMaximum: 100.0,
                },
                // Optional IMS-compatible hints; some platforms will render these in-place
                embed: {
                    html: `<iframe width="800" height="450" src="${previewSrc}" frameborder="0" allowfullscreen></iframe>`,
                },
                iframe: {
                    width: 800,
                    height: 450,
                    src: previewSrc,
                },
            };
        });

        // NEW: a few (3) LTI items that ALSO carry embed.html + iframe (width=800)
        const ltiWithEmbed = Array.from({ length: 3 }, (_, i) => {
            const n = i + 4;
            const title = `LTI Assignment (Embed) ${n}`;
            const slug = toSlug(title);
            const previewSrc = `${backEndUrl}/lti/preview/${slug}`;
            return {
                id: `lti-embed-${n}`,
                type: "ltiResourceLink",
                label: title,
                title,
                url: `${backEndUrl}/lti/launch/${slug}`,
                custom: { assignmentName: title, route: "ShowUserInfo" },
                lineItem: {
                    resourceId: slug,
                    label: `LTI Gradable Item ${title}`,
                    scoreMaximum: 100.0,
                },
                // Optional IMS-compatible hints; some platforms will render these in-place
                embed: {
                    html: `<iframe width="800" height="450" src="${previewSrc}" frameborder="0" allowfullscreen></iframe>`,
                },
                iframe: {
                    width: 800,
                    height: 450,
                    src: previewSrc,
                },
            };
        });

        const html = Array.from({ length: 3 }, (_, j) => {
            const n = j + 1;
            const title = `Embedded HTML ${n}`;
            return {
                id: `html-${n}`,
                type: "html",
                label: title,
                title,
                html: `
<div style="font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;">
  <h3 style="margin:0 0 6px 0;">Welcome Block ${n}</h3>
  <p style="margin:0 0 6px 0;">
    This is a <strong>sample HTML</strong> content item deployed via LTI Deep Linking.
  </p>
  <details style="margin:6px 0;">
    <summary style="cursor:pointer;">More info</summary>
    <p style="margin:6px 0 0;">Expandable details section with a few handy tips.</p>
  </details>
  <ul style="margin:6px 0 0; padding-left:18px;">
    <li>Bullet A</li>
    <li>Bullet B</li>
    <li><a href="https://www.example.com" target="_blank" rel="noopener">Example link</a></li>
  </ul>
</div>`.trim(),
            };
        });

        // Two YouTube examples as IMS "link" items
        const videos = [
            { id: "corV3-WsIro", title: "Sweet Home Alabama" },
            { id: "dQw4w9WgXcQ", title: "Sample Video 2" },
        ];
        const linkWithEmbed = videos.map((v, idx) => ({
            id: `link-${idx + 1}`,
            type: "link",
            label: `YouTube: ${v.title}`,
            title: `YouTube: ${v.title}`,
            url: `https://www.youtube.com/watch?v=${v.id}`,
            embed: {
                html: `<iframe width="560" height="315" src="https://www.youtube.com/embed/${v.id}" frameborder="0" allow="autoplay; encrypted-media" allowfullscreen></iframe>`,
            },
            window: {
                targetName: `youtube-${v.id}`,
                windowFeatures: "height=560,width=315,menubar=no",
            },
            iframe: {
                width: 560,
                height: 315,
                src: `https://www.youtube.com/embed/${v.id}`,
            },
        }));

        return [...lti, ...ltiWithEmbed, ...ltiWithEmbed2, ...html, ...linkWithEmbed];
    }, [backEndUrl]);

    const isSelected = (id) => selectedIds.includes(id);
    const toggleOne = (id) =>
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );

    const selectAll = () => setSelectedIds(items.map((it) => it.id));
    const selectNone = () => setSelectedIds([]);
    const selectType = (type) =>
        setSelectedIds(items.filter((it) => it.type === type).map((it) => it.id));
    const countOf = (type) => items.filter((it) => it.type === type).length;

    const handleDeploy = async () => {
        if (selectedIds.length === 0) {
            setMessage("Please select at least one item to deploy.");
            return;
        }

        // Build IMS-friendly items; include optional embed/iframe for LTI items if present
        const selectedItems = selectedIds
            .map((id) => items.find((it) => it.id === id))
            .filter(Boolean)
            .map((it) => {
                if (it.type === "ltiResourceLink") {
                    const { type, title, url, custom, lineItem, embed, iframe } = it;
                    const out = { type, title, url, custom, lineItem };
                    if (embed) out.embed = embed;
                    if (iframe) out.iframe = iframe;
                    return out;
                } else if (it.type === "html") {
                    const { type, title, html } = it;
                    return { type, title, html };
                } else if (it.type === "link") {
                    const { type, title, url, embed, window: win, iframe } = it;
                    const out = { type, title, url };
                    if (embed) out.embed = embed;
                    if (win) out.window = win;
                    if (iframe) out.iframe = iframe;
                    return out;
                }
                return null;
            })
            .filter(Boolean);

        const payload = { selectedItems };
        // If your backend expects IMS field name: const payload = { content_items: selectedItems };

        try {
            setIsLoading(true);
            setMessage(null);

            const backendResponse = await axios.post(
                `${backEndUrl}/lti/deep-link?state=${encodedState}`,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            const { jwt, deepLinkReturnUrl } = backendResponse?.data || {};
            if (!jwt || !deepLinkReturnUrl) {
                throw new Error("Missing jwt or deepLinkReturnUrl from backend.");
            }

            const form = document.createElement("form");
            form.method = "POST";
            form.action = deepLinkReturnUrl;
            form.enctype = "application/x-www-form-urlencoded";

            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "JWT"; // IMS expects 'JWT'
            input.value = jwt;

            form.appendChild(input);
            document.body.appendChild(form);
            form.submit();
        } catch (error) {
            console.error("Deployment failed:", error);
            setMessage("Deployment failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="deep-link-tool-container" style={{ maxWidth: 860, margin: "0 auto" }}>
            <h1 style={{ marginBottom: 12 }}>Deep Link Tool</h1>

            {/* Toolbar */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    alignItems: "center",
                    marginBottom: 12,
                }}
            >
                <button type="button" onClick={selectAll} style={btn()}>
                    Select all ({items.length})
                </button>
                <button type="button" onClick={() => selectType("ltiResourceLink")} style={btn()}>
                    Select LTI ({countOf("ltiResourceLink")})
                </button>
                <button type="button" onClick={() => selectType("html")} style={btn()}>
                    Select HTML ({countOf("html")})
                </button>
                <button type="button" onClick={() => selectType("link")} style={btn()}>
                    Select Link ({countOf("link")})
                </button>
                <button type="button" onClick={selectNone} style={btnLight()}>
                    Clear
                </button>

                <span
                    style={{
                        marginLeft: "auto",
                        fontSize: 13,
                        padding: "4px 10px",
                        borderRadius: 999,
                        border: "1px solid #d0d7de",
                        background: "#f7f7f7",
                    }}
                >
          Selected: {selectedIds.length}
        </span>
            </div>

            {message && (
                <div
                    className="message"
                    style={{
                        marginBottom: 12,
                        border: "1px solid #ffe58f",
                        background: "#fffbe6",
                        padding: 10,
                        borderRadius: 8,
                        fontSize: 14,
                    }}
                >
                    {message}
                </div>
            )}

            {/* List */}
            <div className="item-list" style={{ display: "grid", gap: 8 }}>
                {items.map((item) => {
                    const isHtml = item.type === "html";
                    const isLink = item.type === "link";
                    const isLti = item.type === "ltiResourceLink";
                    const isLtiWithEmbed = isLti && (item.embed || item.iframe);
                    return (
                        <div
                            key={item.id}
                            className="item"
                            style={{
                                border: "1px solid #e3e3e3",
                                borderRadius: 8,
                                padding: 12,
                                display: "grid",
                                gridTemplateColumns: "24px 1fr auto",
                                gap: 10,
                                alignItems: "start",
                            }}
                        >
                            {/* Checkbox */}
                            <input
                                type="checkbox"
                                aria-label={`Select ${item.label}`}
                                checked={isSelected(item.id)}
                                onChange={() => toggleOne(item.id)}
                                style={{ width: 18, height: 18, marginTop: 4, cursor: "pointer" }}
                            />

                            {/* Main */}
                            <div>
                                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                    <span style={{ fontWeight: 600 }}>{item.label}</span>
                                    <span
                                        title={item.type}
                                        style={{
                                            fontSize: 12,
                                            padding: "2px 8px",
                                            borderRadius: 999,
                                            border: "1px solid #d0d7de",
                                            background: "#fafafa",
                                        }}
                                    >
                    {isHtml ? "HTML" : isLink ? "LINK+EMBED" : isLtiWithEmbed ? "LTI+EMBED" : "LTI"}
                  </span>
                                </div>

                                {isHtml ? (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            background: "#fafafa",
                                            border: "1px dashed #ddd",
                                            borderRadius: 6,
                                            padding: 8,
                                            marginTop: 6,
                                            overflowX: "auto",
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        <strong>Preview HTML:</strong>
                                        <div style={{ marginTop: 6 }}>
                                            <code>
                                                {item.html.slice(0, 180)}
                                                {item.html.length > 180 ? "…" : ""}
                                            </code>
                                        </div>
                                    </div>
                                ) : isLink ? (
                                    <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>
                                        <div>
                                            <strong>URL:</strong> {item.url}
                                        </div>
                                        {item.iframe && (
                                            <div style={{ marginTop: 4 }}>
                                                <strong>iFrame:</strong> {item.iframe.src} ({item.iframe.width}×{item.iframe.height})
                                            </div>
                                        )}
                                        {item.embed?.html && (
                                            <div
                                                style={{
                                                    marginTop: 6,
                                                    background: "#fafafa",
                                                    border: "1px dashed #ddd",
                                                    borderRadius: 6,
                                                    padding: 8,
                                                    overflowX: "auto",
                                                    whiteSpace: "pre-wrap",
                                                }}
                                            >
                                                <strong>Embed HTML:</strong>
                                                <div style={{ marginTop: 6 }}>
                                                    <code>
                                                        {item.embed.html.slice(0, 180)}
                                                        {item.embed.html.length > 180 ? "…" : ""}
                                                    </code>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>
                                        <div>
                                            <strong>Launch URL:</strong> {item.url}
                                        </div>
                                        <div>
                                            <strong>Score Max:</strong>{" "}
                                            {item.lineItem?.scoreMaximum != null ? item.lineItem.scoreMaximum : "—"}
                                        </div>

                                        {/* Show LTI embed details if present */}
                                        {(item.iframe || item.embed?.html) && (
                                            <div style={{ marginTop: 6 }}>
                                                {item.iframe && (
                                                    <div>
                                                        <strong>iFrame:</strong> {item.iframe.src} ({item.iframe.width}×{item.iframe.height})
                                                    </div>
                                                )}
                                                {item.embed?.html && (
                                                    <div
                                                        style={{
                                                            marginTop: 6,
                                                            background: "#fafafa",
                                                            border: "1px dashed #ddd",
                                                            borderRadius: 6,
                                                            padding: 8,
                                                            overflowX: "auto",
                                                            whiteSpace: "pre-wrap",
                                                        }}
                                                    >
                                                        <strong>Embed HTML:</strong>
                                                        <div style={{ marginTop: 6 }}>
                                                            <code>
                                                                {item.embed.html.slice(0, 180)}
                                                                {item.embed.html.length > 180 ? "…" : ""}
                                                            </code>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Quick toggle */}
                            <button
                                type="button"
                                onClick={() => toggleOne(item.id)}
                                style={isSelected(item.id) ? btnLight() : btn()}
                            >
                                {isSelected(item.id) ? "Unselect" : "Select"}
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Footer actions */}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button
                    onClick={handleDeploy}
                    className="deploy-button"
                    disabled={isLoading || selectedIds.length === 0}
                    style={{
                        padding: "10px 16px",
                        borderRadius: 8,
                        border: "1px solid #222",
                        background: isLoading || selectedIds.length === 0 ? "#f3f3f3" : "#111",
                        color: isLoading || selectedIds.length === 0 ? "#777" : "#fff",
                        cursor: isLoading || selectedIds.length === 0 ? "not-allowed" : "pointer",
                    }}
                    title={selectedIds.length === 0 ? "Select at least one item" : undefined}
                >
                    {isLoading ? "Deploying…" : "Deploy to Blackboard"}
                </button>
            </div>
        </div>
    );
};

function btn() {
    return {
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #1f2328",
        background: "#1f2328",
        color: "#fff",
        cursor: "pointer",
        fontSize: 14,
    };
}

function btnLight() {
    return {
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #d0d7de",
        background: "#fff",
        color: "#111",
        cursor: "pointer",
        fontSize: 14,
    };
}

export default DeepLinkTool;
